import { useCallback, useEffect, useState } from 'react';

import { AuthError, errorKey, type AuthApi, type Mail } from '../auth/api';
import type { MessageKey } from '../i18n/i18n.ts';
import { useI18n } from '../i18n/I18nProvider';
import { Button } from '../ui/Button';
import { Sheet } from '../ui/Sheet';
import type { GameApi } from './api';
import './mail.css';

/**
 * 站内信。**只收系统发来的信**（提现结果、后台发的通知和补偿），玩家之间不能互发。
 *
 * **文案在这里翻，不在后端。** 后端给的是 `tpl` + `params` —— 玩家会换语言，
 * 而信箱里的是历史记录：存成葡语的话他换成中文之后就永远是葡语了。
 * 和后端错误按 `code` 翻是同一条规矩。
 *
 * 运营手写的信没有模板，只有 `title` / `body`，原样显示。
 * 那两个字段是**别人写的文本**（运营、以及被带进 params 的渠道报错），
 * 所以一律走 React 的文本节点 —— 这个文件里没有 `dangerouslySetInnerHTML`，
 * 以后也不该有。
 *
 * ## 附件
 *
 * 后台发的信可以带附件：金币 + 打码量。**领取走 gateway**（`mail_claim`），
 * 请求里只有信的 id —— 金额由服务端从那封信读，这里传什么都不算。
 *
 * 打开就把当前这一页标成已读：信箱不是收件箱，没有"我先留着不读"的需求，
 * 而一个点进去还挂着红点的信箱比没有红点更让人烦。**标已读不等于领了附件** ——
 * 附件要他自己点。
 */
export function MailSheet(
  { open, api, game, token, onClose, money, onUnread, onClaimed }:
  {
    open: boolean;
    api: AuthApi;
    game: GameApi;
    token: string;
    onClose(): void;
    money(n: number): string;
    /** 未读数变了就告诉外面 —— 入口上那个红点要跟着变 */
    onUnread(n: number): void;
    /** 领了附件 —— 外面的余额要重拉 */
    onClaimed?(): void;
  },
) {
  const { t, i18n } = useI18n();
  const [items, setItems] = useState<Mail[] | null>(null);
  const [before, setBefore] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [claiming, setClaiming] = useState<number | null>(null);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');

  const load = useCallback(async (cursor: number | null, append: boolean) => {
    setBusy(true);
    try {
      const page = await api.mail(token, cursor);
      setItems((prev) => (append && prev ? [...prev, ...page.items] : page.items));
      setBefore(page.nextBefore);
      setErr('');

      // 这一页里没读过的，读了。**只标这一页** —— 用 all 的话，
      // 下面几页他还没看到的信也会被标掉。
      //
      // **但界面上不跟着改。** 服务端那边标掉了（红点会消），这一屏里
      // 那几条仍然带着"新"的标记 —— 不然它会在他眼皮底下闪一下就没了，
      // 而他打开信箱要看的恰恰是"哪几条是新的"。下次再打开就是已读的样子。
      const unread = page.items.filter((m) => !m.read).map((m) => m.id);
      if (unread.length > 0) {
        const r = await api.readMail(token, { ids: unread });
        onUnread(r.unread);
      } else {
        onUnread(page.unread);
      }
    } catch (e) {
      setErr(t(errorKey(e)));
    } finally { setBusy(false); }
  }, [api, token, t, onUnread]);

  useEffect(() => {
    if (!open) return;
    setItems(null);
    setOk('');
    void load(null, false);
  }, [open, load]);

  const markClaimed = (id: number) => setItems((prev) => prev?.map((x) => (
    x.id === id && x.attachment ? { ...x, attachment: { ...x.attachment, claimed: true } } : x
  )) ?? prev);

  async function claim(m: Mail) {
    setClaiming(m.id);
    setErr('');
    setOk('');
    try {
      const r = await game.claimMail(token, m.id);
      markClaimed(m.id);
      setOk(r.wager > 0
        ? t('mail.claimedOkWager', { v: money(r.coins), w: money(r.wager) })
        : t('mail.claimedOk', { v: money(r.coins) }));
      onClaimed?.();
    } catch (e) {
      // 在另一台设备上领过了：按已领显示。**不回滚成"可领"** ——
      // 那会让他以为钱没到，再点一次还是这句话
      if (e instanceof AuthError && e.code === 'ALREADY_CLAIMED') markClaimed(m.id);
      setErr(t(errorKey(e)));
    } finally {
      setClaiming(null);
    }
  }

  return (
    <Sheet open={open} title={t('mail.title')} onClose={onClose}>
      <div className="stack">
        {items === null && !err && <p className="small dim">{t('action.working')}</p>}
        {items?.length === 0 && <p className="small dim">{t('mail.empty')}</p>}
        {ok && <p className="small mail__ok" role="status">{ok}</p>}

        {items && items.length > 0 && (
          <ul className="mail__list">
            {items.map((m) => (
              <li key={m.id} className={`mail__item${m.read ? '' : ' mail__item--new'}`}>
                <div className="spread">
                  <span className="mail__subject">{subject(m, t)}</span>
                  {!m.read && <span className="mail__dot" aria-hidden="true" />}
                </div>
                <p className="small mail__body">{body(m, t, money)}</p>
                <span className="small dim">
                  {m.ts ? i18n.dateTime(m.ts * 1000) : ''}
                </span>
                {/* 渠道/运营写的那句话没法翻译，只能原样带上 ——
                    所以它单独一行，和上面翻译过的正文分开。 */}
                {extra(m) && <p className="small dim mail__extra">{extra(m)}</p>}

                {m.attachment && (
                  <div className="mail__attach">
                    <div className="mail__attach-items">
                      <span className="small dim">{t('mail.attachment')}</span>
                      <span className="mail__coins num">
                        {t('mail.coins', { v: money(m.attachment.coins) })}
                      </span>
                      {m.attachment.wager > 0 && (
                        <span className="small num">
                          {t('mail.wager', { v: money(m.attachment.wager) })}
                        </span>
                      )}
                    </div>
                    {m.attachment.claimed ? (
                      <span className="small dim">{t('mail.claimed')}</span>
                    ) : (
                      <Button variant="primary" disabled={claiming !== null}
                              onClick={() => { void claim(m); }}>
                        {claiming === m.id ? t('action.working') : t('mail.claim')}
                      </Button>
                    )}
                    {/* 打码量是提现门槛。**领之前就说清楚**，不然领完去提现被拦会来问客服 */}
                    {!m.attachment.claimed && m.attachment.wager > 0 && (
                      <p className="small dim mail__note">{t('mail.wagerNote')}</p>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {before !== null && (
          <Button variant="ghost" disabled={busy} className="btn--wide"
                  onClick={() => { void load(before, true); }}>
            {busy ? t('action.working') : t('action.more')}
          </Button>
        )}
        {err && <p className="field__err" role="alert">{err}</p>}
      </div>
    </Sheet>
  );
}

type T = (k: MessageKey, p?: Record<string, string | number>) => string;

/**
 * 后端给的 `tpl` 是一个**运行时**的字符串，而文案表的键是编译期的联合类型。
 * 断言在这一个地方发生，别处都用真类型 —— 认不出来的键由 `t()` 自己兜底
 * （它取不到时返回键名，不抛），下面 `body()` 再把它换成一句通用文案。
 */
const key = (s: string) => s as MessageKey;

/** 标题。运营写的信用它自己的，模板信按 `tpl` 翻。 */
function subject(m: Mail, t: T): string {
  if (m.title) return m.title;
  if (!m.tpl) return '';
  const k = `${m.tpl}.title`;
  const s = t(key(k));
  return s === k ? t('mail.unknown.title') : s;
}

/**
 * 正文。
 *
 * **认不出来的模板不留空白。** 后端加了一封新模板信而前端还没发版时，
 * 这里会退化成一句通用的"你有一条新消息" —— 空白的一封信会让玩家以为
 * 出了故障，而它其实只是版本差了一点。
 */
function body(m: Mail, t: T, money: (n: number) => string): string {
  if (m.body) return m.body;
  if (!m.tpl) return '';
  const p = m.params || {};
  // 金额在 params 里是字符串（JSONB 里的浮点会变成 492.50000000000006）。
  // 格式化在这里做 —— 符号和小数位是服务端下发的币种信息，money() 拿着它。
  const num = (k: string) => money(Number(p[k] ?? 0));
  const args = { id: String(p.id ?? ''), amount: num('amount'),
                 fee: num('fee'), net: num('net') };
  const k = `${m.tpl}.body`;
  const s = t(key(k), args);
  return s === k ? t('mail.unknown.body') : s;
}

/** 第三方写的那句话（渠道失败原因、运营驳回备注）。没法翻译，原样显示。 */
function extra(m: Mail): string {
  const p = m.params || {};
  return String(p.reason || p.note || '');
}
